import uniqBy from "lodash-es/uniqBy";
import * as Moment from "moment";
import * as React from "react";
import { QueryCondition, QueryFilter } from "../../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { LocalQueryTable } from "../../../misc/components/queryTable/LocalQueryTable";
import MultiSelectModal from "../../../misc/components/queryTable/MultiSelectModal";
import { QueryTableColumn } from "../../../misc/components/queryTable/QueryTable";
import { TimeRangeModal } from "../../../misc/components/queryTable/TimeRangeModal";
import { instanceOfPeaksPoint, PeaksPeriod, PeaksPoint } from "../interfacesPeaks/PeaksJS";
import { formatToHHmmss_ss, HHmmssToTotalSeconds } from "../../../misc/timeFormatting";
import { Query } from '../../../misc/components/queryTable/Query';

interface EventListTableProps {
    timestampColumn: "timestamp" | "startTime" | "rawTimestamp";
    startTimeColumnName: "Start Time" | "Waveform Time";
    withEndTime: boolean;
    events: Array<PeaksPoint | PeaksPeriod>;
    renderRawTime?: boolean;
    onJumpToEventClick: (timepoint: number) => void;
    onDeleteEventClick: (id: string) => void;
    query: Query<any>;
}

interface EventListTableState {
    showEventTypeFilter: boolean;
    showStartTimeFilter: boolean;
    showRawTimeFilter: boolean;
    filters: QueryFilter[];
    startFilterValue: string;
    endFilterValue: string;
}

export class EventListTable extends React.Component<EventListTableProps, EventListTableState> {
    childRef: any;

    constructor(props: EventListTableProps) {
        super(props);

        this.state = {
            showEventTypeFilter: false,
            showStartTimeFilter: false,
            showRawTimeFilter: false,
            filters: props.query ? props.query.Filters : [],
            startFilterValue: null,
            endFilterValue: null
        };
    }

    getTableQuery() {
        return this.childRef ? this.childRef.getQuery(): null;
    }

    public renderLabel = (event: PeaksPoint | PeaksPeriod) => {
        return (
            <>
                <i className="fa fa-square" style={{ color: event.color }} />&nbsp;
                {event.eventTypeName}
            </>
        );
    };

    renderHamburgerMenu = (event: PeaksPoint & PeaksPeriod) => {
        return (
            <>
                <button
                    className="btn btn-primary btn-xs"
                    onClick={() =>
                        this.props.onJumpToEventClick(
                            event.isPoint ? event.timestamp : event.startTime
                        )
                    }
                >
                    <i className="fa fa-share" />
                </button>
                &nbsp;
                <button
                    className="btn btn-danger btn-xs"
                    disabled={!event.editable}
                    onClick={() => this.props.onDeleteEventClick(event.id)}
                >
                    <i className="fa fa-trash" />
                </button>
            </>
        );
    };

    handleFilter = (member: keyof (PeaksPoint & PeaksPeriod)) => {
        switch (member) {
            case "eventTypeName":
                this.setState({ showEventTypeFilter: true });
                break;
            case "timestamp":
            case "startTime":
                this.setState({ showStartTimeFilter: true });
                break;
            case "rawTimestamp":
                this.setState({ showRawTimeFilter: true });
                break;
            default:
                break;
        }
    };

    handleFilterClose = () => {
        this.setState({
            showEventTypeFilter: false,
            showStartTimeFilter: false,
            showRawTimeFilter: false
        });
    };

    handleMultiSelectFilterChange = (
        member: keyof (PeaksPoint | PeaksPeriod),
        values: string[]
    ) => {
        let eventTypeNameFilter = this.state.filters.find(x => x.Member === "eventTypeName");
        if (eventTypeNameFilter) {
            if (values.length > 0) {
                eventTypeNameFilter.Values = values;
            } else {
                eventTypeNameFilter = null;
            }
        } else {
            eventTypeNameFilter = {
                Member: "eventTypeName",
                Condition: QueryCondition.Equal,
                Values: values
            };
        }

        const newFilters = [
            ...this.state.filters.filter(x => x.Member !== "eventTypeName"),
            eventTypeNameFilter
        ];

        this.setState({
            filters: newFilters.filter(x => x) // Get rid of undefined or nulls
        });
    };

    public handleDateRangeFilterChange = (from: string, to: string, member: string) => {
        let fromFilter = this.state.filters.find(
            x => x.Member === member && x.Condition === QueryCondition.GreaterThan
        );

        if (fromFilter && from) {
            fromFilter.Values = [from];
        } else if (from === null) {
            fromFilter = null;
        } else {
            fromFilter = {
                Member: member,
                Condition: QueryCondition.GreaterThan,
                Values: [from]
            };
        }

        let toFilter = this.state.filters.find(
            x => x.Member === member && x.Condition === QueryCondition.LessThanOrEqual
        );

        if (toFilter && to) {
            toFilter.Values = [to];
        } else if (to === null) {
            toFilter = null;
        } else {
            toFilter = {
                Member: member,
                Condition: QueryCondition.LessThanOrEqual,
                Values: [to]
            };
        }

        const newFilters = [
            ...this.state.filters.filter(x => x.Member === "eventTypeName"),
            fromFilter,
            toFilter
        ];

        this.setState({
            startFilterValue: from,
            endFilterValue: to,
            filters: newFilters.filter(x => x) // Get rid of undefined or nulls
        });
    };

    padDatePart = (datePart: number): string => {
        return ("0" + datePart).slice(-2);
    };

    filterDatesCallback = (
        event: PeaksPoint | PeaksPeriod,
        totalFromSeconds: number,
        totalToSeconds: number
    ): boolean => {
        if (instanceOfPeaksPoint(event)) {
            const rawOrRelativeTimestamp = this.state.filters.some(x => x.Member === "rawTimestamp")
                ? event.rawTimestamp
                : event.timestamp;

            return (
                rawOrRelativeTimestamp > totalFromSeconds &&
                rawOrRelativeTimestamp <= totalToSeconds
            );
        } else {
            return event.startTime > totalFromSeconds && event.startTime <= totalToSeconds;
        }
    };

    filterEventTypes = (
        filterValues: string[]
    ) => {
        let filteredEvents: Array<PeaksPoint | PeaksPeriod>;
        if (filterValues.length > 0) {
            filteredEvents = this.props.events.filter(x => filterValues.includes(x["eventTypeId"]));
        } else {
            filteredEvents = this.props.events;
        }
        return filteredEvents;
    };

    render() {
        if (this.props.events.length === 0) {
            return (
                <Alert show={true} category={AlertCategoryEnum.Success}>
                    There are no events currently marked
                </Alert>
            );
        }

        let {
            // tslint:disable-next-line:prefer-const
            filteredEvents,
            // tslint:disable-next-line:prefer-const
            eventTypeFilterValues
        } = this.getFilteredEventsAndFilterValues();

        filteredEvents = this.filterByTime(filteredEvents);

        return (
            <>
                <LocalQueryTable
                    collection={filteredEvents}
                    defaultPageSize={10}
                    onFilter={this.handleFilter}
                    filters={this.state.filters}
                    ref={childRef => {
                        this.childRef = childRef;
                    }}
                    query={this.props.query}
                    rowUniqueIdentifier={row => row.id}
                >
                    <QueryTableColumn
                        member="eventTypeName"
                        header="Event Type"
                        map={(row: PeaksPoint & PeaksPeriod) => this.renderLabel(row)}
                        sortable={true}
                        filterable={true}
                    />
                    {this.renderRawTimeColumn()}
                    <QueryTableColumn
                        member={this.props.timestampColumn}
                        header={this.props.startTimeColumnName}
                        map={(row: PeaksPoint & PeaksPeriod) =>
                            row.isPoint
                                ? formatToHHmmss_ss(row.timestamp)
                                : formatToHHmmss_ss(row.startTime)
                        }
                        sortable={true}
                        filterable={true}
                    />
                    {this.renderEndTimeColumn()}
                    <QueryTableColumn
                        member=""
                        header=""
                        map={row => this.renderHamburgerMenu(row)}
                    />
                </LocalQueryTable>
                {this.renderFilterModals(eventTypeFilterValues)}
            </>
        );
    }
    private renderFilterModals(eventTypeFilterValues: string[]) {
        return (
            <>
                <MultiSelectModal
                    header="Event Type Filter"
                    show={this.state.showEventTypeFilter}
                    options={this.getEventTypeOptions()}
                    valueKey="Value"
                    labelKey="Label"
                    values={eventTypeFilterValues}
                    onChange={values => this.handleMultiSelectFilterChange("eventTypeId", values.map(v => v.Value))}
                    onClose={this.handleFilterClose}
                />
                <TimeRangeModal
                    header="Raw Time Filter"
                    show={this.state.showRawTimeFilter}
                    from={this.state.startFilterValue}
                    to={this.state.endFilterValue}
                    onChange={(from, to) =>
                        this.handleDateRangeFilterChange(from, to, "rawTimestamp")
                    }
                    onClose={this.handleFilterClose}
                />
                <TimeRangeModal
                    header={`${this.props.startTimeColumnName} Filter`}
                    show={this.state.showStartTimeFilter}
                    from={this.state.startFilterValue}
                    to={this.state.endFilterValue}
                    onChange={(from, to) => this.handleDateRangeFilterChange(from, to, "timestamp")}
                    onClose={this.handleFilterClose}
                />
            </>
        );
    }

    private renderRawTimeColumn() {
        return this.props.renderRawTime ? (
            <QueryTableColumn
                member="rawTimestamp"
                header="Raw Time"
                map={(row: PeaksPoint & PeaksPeriod) =>
                    row.isPoint
                        ? formatToHHmmss_ss(row.rawTimestamp)
                        : formatToHHmmss_ss(row.startTime)
                }
                sortable={true}
                filterable={true}
            />
        ) : null;
    }

    private getFilteredEventsForFilteredDates = (
        filteredEvents: Array<PeaksPoint | PeaksPeriod>,
        startTotalSeconds: number,
        endTotalSeconds: number
    ) =>
        filteredEvents.filter(x => this.filterDatesCallback(x, startTotalSeconds, endTotalSeconds));

    private filterByTime(filteredEvents: Array<PeaksPoint | PeaksPeriod>) {
        if (this.state.startFilterValue || this.state.endFilterValue) {
            filteredEvents = this.getFilteredEventsForFilteredDates(
                filteredEvents,
                HHmmssToTotalSeconds(this.state.startFilterValue),
                HHmmssToTotalSeconds(this.state.endFilterValue)
            );
        }
        return filteredEvents;
    }

    private getFilteredEventsAndFilterValues() {
        let filteredEvents: Array<PeaksPoint | PeaksPeriod> = [];

        const eventTypeFilterValues = [
            ...this.state.filters
                .filter(x => x.Member === "eventTypeName")
                .map(y => y.Values.map(v => String(v))).flat()
        ];

        filteredEvents = this.filterEventTypes(eventTypeFilterValues);

        return { filteredEvents, eventTypeFilterValues };
    }

    private getEventTypeOptions() {
        return uniqBy(this.props.events, e => e.eventTypeId).map(m => {
            return { Label: m.eventTypeName, Value: m.eventTypeId };
        });
    }

    private renderEndTimeColumn() {
        return this.props.withEndTime ? (
            <QueryTableColumn
                member="endTime"
                header="End Time"
                map={(row: PeaksPoint & PeaksPeriod) =>
                    row.isPoint ? "" : formatToHHmmss_ss(row.endTime)
                }
                sortable={true}
                filterable={false}
            />
        ) : null;
    }
}
